import { formatMoney, tonStringFromPounds, formatPercentage, numberWithCommas, mbfStringFromBf } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { PayOn, PayBy, ActivityModifier, CorrectionTypes, CorrectionSide, RecoveryType, ContractType, ScaleDataSource, SettlementReportMode } from '@/utils/Enumerations.js'
import { recoveryRateText } from '@/utils/FinancialHelpers.js'
import { scatterToObject, padArrayTo } from '../base'
import i18n from '@/i18n'
import store from '@/store/index.js'
import { PdfLogoDimensions } from '../CompanyLogo.js'
import { DefectCategory } from '../Enumerations'
import moment from 'moment/moment'
import { wrapTextAtMaxCharacters } from '@/utils/TextWrapHelpers.js'
import { NumericConstants } from '@/utils/constants.js'
import { generateSvgForPdf, rulerSquare, alphaECircleOutline, fileImport } from '@/images/AngelSvgLibrary'
import { alternatingRowColorsLayout } from '@/utils/pdf-formatters/pdfHelpers'

// Cell border styles.
const noBorder = [false, false, false, false]
const topBorder = [false, true, false, false]
const bottomBorder = [false, false, false, true]

export function getHeader ({ logoAsDataUrl, logoTopMargin }) {
  const companyInfo = store.getters['user/companyInfo']
  const companyName = companyInfo.name ? companyInfo.name : ''
  const companyAddress = formatAddress(companyInfo)
  const companyPhoneNumber = companyInfo.mainContact && companyInfo.mainContact.phoneNumber ? companyInfo.mainContact.phoneNumber : ''
  const companyInfoSection = (logoAsDataUrl === undefined || logoTopMargin === undefined)
    ? [
      {
        table: {
          body: [
            [''],
            [{ text: companyName, alignment: 'left' }],
            [{ text: companyAddress, alignment: 'left', fontSize: 8 }],
            [{ text: companyPhoneNumber, alignment: 'left', fontSize: 8 }]
          ]
        },
        layout: 'noBorders'
      }
    ]
    : [
      {
        image: logoAsDataUrl,
        maxWidth: PdfLogoDimensions.LOGO_MAX_WIDTH,
        maxHeight: PdfLogoDimensions.LOGO_MAX_HEIGHT,
        marginTop: logoTopMargin,
        alignment: 'center'
      },
      {
        table: {
          body: [
            [''],
            [{ text: companyName, alignment: 'left' }],
            [{ text: companyAddress, alignment: 'left', fontSize: 8 }],
            [{ text: companyPhoneNumber, alignment: 'left', fontSize: 8 }]
          ]
        },
        layout: 'noBorders'
      }
    ]
  return {
    fontSize: 15,
    bold: true,
    marginLeft: 40,
    marginRight: 40,
    table: {
      widths: '*',
      body: [
        [
          {
            table: {
              body: [
                companyInfoSection
              ]
            },
            layout: 'noBorders'
          },
          {
            alignment: 'center',
            table: {
              widths: '*',
              body: [
                [''],
                [''],
                [{ text: 'Settlement Statement', fontSize: 15, alignment: 'center' }]
              ]
            },
            layout: 'noBorders'
          },
          { text: '' }
        ]
      ]
    },
    layout: 'noBorders'
  }
}

export function formatAddress (companyInfo) {
  if (companyInfo.address && companyInfo.address.addressLine1 && companyInfo.address.city && companyInfo.address.state && companyInfo.address.postalCode) {
    return companyInfo.address.addressLine1 + '\n' + companyInfo.address.city + ', ' + companyInfo.address.state + ' ' + companyInfo.address.postalCode
  } else {
    return ''
  }
}

export function getContractTable (info) {
  const tractCountyState = (subdivision, secondarySubdivision) => (secondarySubdivision || secondarySubdivision ? ` (${secondarySubdivision}, ${subdivision})` : '')

  const hasTract = !(info.type === ContractType.ByproductSale.value ||
    info.type === ContractType.ByproductPurchase.value ||
    info.type === ContractType.Transfer.value ||
    info.type === ContractType.LogYardSale.value ||
    info.type === ContractType.ByproductHauling.value)

  const body = !hasTract
    ? [
      (info.type === ContractType.Transfer.value || info.type === ContractType.LogYardSale.value)
        ? [{ text: 'Type: ' }, { text: ContractType.fromInt(info.type) }]
        : undefined,
      [{ text: 'Account: ' }, { text: info.accountName }],
      [{ text: 'From Account: ' }, { text: info.fromAccountName }],
      (info.distance > 0)
        ? [{ text: 'Destination: ' }, { text: `${info.destinationName} (${info.distance}mi.)` }]
        : [{ text: 'Destination: ' }, { text: `${info.destinationName}` }]
    ].filter(l => l !== undefined)
    : [
      [{ text: 'Account: ' }, { text: `${info.accountName}` }],
      [{ text: 'Tract: ' }, { text: `${info.tractName} ${tractCountyState(info.countrySubdivision, info.countrySecondarySubdivision)}` }],
      (info.distance > 0)
        ? [{ text: 'Destination: ' }, { text: `${info.destinationName} (${info.distance}mi.)` }]
        : [{ text: 'Destination: ' }, { text: `${info.destinationName}` }],
      [{ text: 'Setting: ' }, { text: `${info.settingName}` }],
      [{ text: 'Logger: ' }, { text: info.loggerName || 'N/A' }]
    ]

  return {
    table: {
      body
    },
    layout: 'noBorders'
  }
}

export function getPaymentRollupTable (paymentRollup, distance, contractType, params = {}) {
  const { settlementReportMode = SettlementReportMode.Standard.value } = params
  const recoveryPresent = paymentRollup.some(pr => pr.grossPayout !== pr.payout)
  const includeRecoveries = !(contractType === ContractType.Transfer.value || contractType === ContractType.LogYardSale.value) &&
    (settlementReportMode !== SettlementReportMode.Brief.value || recoveryPresent)
  const paddedRow = padArrayTo.bind(this, includeRecoveries ? 8 : 7, { text: '', border: noBorder })
  const rowPaddedWith = padArrayTo.bind(this, includeRecoveries ? 8 : 7)
  const notUndefined = d => d !== undefined
  const withDefaults = defaults => r => ({ ...defaults, ...r })
  const keys = rowPaddedWith({ text: '' }, {
    start: [
      { text: 'Activity' },
      { text: 'Product' },
      { text: 'Loads', alignment: 'right' },
      { text: 'Unit', alignment: 'right' }
    ],
    end: [
      { text: 'Rate', alignment: 'right' },
      { text: 'Gross Payout', alignment: 'right' },
      includeRecoveries ? { text: 'Recovered', alignment: 'right' } : undefined,
      { text: 'Net Payout', alignment: 'right' }
    ].filter(notUndefined)
  }).map(withDefaults({ bold: true, border: bottomBorder }))
  const values = []
  const totalPayouts = paymentRollup.reduce((total, item) => {
    total.net += item.payout
    total.gross += item.grossPayout
    return total
  }, { net: 0, gross: 0 })
  paymentRollup.forEach(pr => {
    const prRate = pr.isDeduction ? Math.abs(pr.averageRate) : pr.averageRate
    const rateText = pr.modifier === ActivityModifier.None.value
      ? `${formatMoney(prRate)}`
      : `${formatMoney(prRate)} (${distance}mi.)`
    pr.productSummaries.forEach(ps => {
      let psRate = formatMoney(ps.rate)
      if (pr.payBy === PayBy.Volume.value) {
        const psPayout = pr.isDeduction ? Math.abs(ps.payout) : ps.payout
        psRate = ps.payOn === PayOn.Gross.value
          ? formatMoney(psPayout / (ps.grossVolume / NumericConstants.MbfDivisor))
          : formatMoney(psPayout / (ps.netVolume / NumericConstants.MbfDivisor))
      }

      values.push(paddedRow({
        start: [
          { text: pr.activityName },
          { text: ps.product },
          { text: ps.loadCount, alignment: 'right' },
          { text: getUnitTextForActivity({ payOn: pr.payOn, payBy: pr.payBy, netVolume: pr.netVolume, grossVolume: pr.grossVolume, ...ps }, distance, false), alignment: 'right' }
        ],
        end: [
          { text: psRate, alignment: 'right' },
          { text: formatMoney(ps.grossPayout), alignment: 'right' },
          includeRecoveries
            ? { text: formatMoney(Math.abs(ps.grossPayout - ps.payout)), alignment: 'right' }
            : undefined,
          { text: formatMoney(ps.payout), alignment: 'right' }
        ].filter(notUndefined)
      }).map(r => ({
        border: noBorder,
        ...r
      })))
    })

    const unitString = getUnitTextForActivity(pr, distance)

    values.push(paddedRow({
      start: [
        { text: pr.activityName },
        { text: 'Total' },
        { text: pr.loadCount, alignment: 'right' },
        { text: unitString, alignment: 'right' }
      ],
      end: [
        { text: rateText, alignment: 'right' },
        { text: formatMoney(pr.grossPayout), alignment: 'right' },
        (includeRecoveries)
          ? { text: formatMoney(Math.abs(pr.grossPayout - pr.payout)), alignment: 'right' }
          : undefined,
        { text: formatMoney(pr.payout), alignment: 'right' }
      ].filter(notUndefined)
    }).map(r => ({
      bold: true,
      border: bottomBorder,
      ...r
    })))
  })

  const footerRowStyling = { alignment: 'right', bold: true, fontSize: 9, border: topBorder }
  values.push(rowPaddedWith('', {
    start: [
      'Total'
    ],
    end: [
      formatMoney(totalPayouts.gross),
      (includeRecoveries)
        ? formatMoney(totalPayouts.gross - totalPayouts.net)
        : undefined,
      formatMoney(totalPayouts.net)
    ].filter(notUndefined)
  }).map((r, i) => ({
    text: r,
    ...footerRowStyling,
    alignment: i === 0 ? 'left' : 'right'
  })))
  return [
    {
      table: {
        widths: ['*', '*', 'auto', '*', 'auto', 'auto', 'auto', includeRecoveries ? 'auto' : undefined].filter(notUndefined),
        body: [
          [
            { text: 'Payment Detail', border: noBorder, fontSize: 15, bold: true },
            { text: '', border: noBorder },
            { text: '', border: noBorder },
            { text: '', border: noBorder },
            { text: '', border: noBorder },
            { text: '', border: noBorder },
            { text: '', border: noBorder },
            includeRecoveries ? { text: '', border: noBorder } : undefined
          ].filter(notUndefined),
          keys,
          ...values
        ]
      },
      style: 'nestedTable'
    }
  ]
}

export function getLoadSummaryTable (loadSummaries, params = {}) {
  const { settlementReportMode = SettlementReportMode.Standard.value, contractType } = params
  if (settlementReportMode === SettlementReportMode.Brief.value) {
    return getBriefLoadSummaryTable(loadSummaries, isByproductContract(contractType))
  }

  const paysByVolume = loadSummaries.some(ls =>
    ls.paymentDetails?.some(pd => pd.computedAtPayBy === PayBy.Volume.value)
  )
  const totalRecovered = loadSummaries.reduce((sum, ls) =>
    sum + ls.paymentDetails.reduce((s, pd) => s + (pd.grossAmount - pd.amount), 0), 0
  )
  const showRecovered = totalRecovered !== 0
  const totalDefectTons = loadSummaries.reduce((sum, ls) => sum + (ls.defectWeight ?? 0), 0)
  const showDefectTons = !paysByVolume && totalDefectTons !== 0

  const headerRow = [
    { text: '#', bold: true, border: bottomBorder },
    { text: 'Delivered', bold: true, border: bottomBorder },
    { text: 'Activity', bold: true, border: bottomBorder },
    { text: 'Product', bold: true, border: bottomBorder },
    { text: 'Gross Tons', alignment: 'right', bold: true, border: bottomBorder },
    ...(showDefectTons ? [{ text: 'Defect Tons', alignment: 'right', bold: true, border: bottomBorder }] : []),
    { text: 'Net Tons', alignment: 'right', bold: true, border: bottomBorder },
    ...(paysByVolume ? [
      { text: 'Gross MBF', alignment: 'right', bold: true, border: bottomBorder },
      { text: 'Net MBF', alignment: 'right', bold: true, border: bottomBorder }
    ] : []),
    { text: 'Pay On', bold: true, alignment: 'right', border: bottomBorder },
    { text: 'Rate', bold: true, alignment: 'right', border: bottomBorder },
    ...(showRecovered ? [{ text: 'Recovered', bold: true, alignment: 'right', border: bottomBorder }] : []),
    { text: 'Amount', bold: true, alignment: 'right', border: bottomBorder }
  ]
  const widths = [
    'auto', 'auto', '*', '*', 'auto',
    ...(showDefectTons ? ['auto'] : []),
    'auto',
    ...(paysByVolume ? ['auto', 'auto'] : []),
    'auto', 'auto',
    ...(showRecovered ? ['auto'] : []),
    'auto'
  ]

  const body = [headerRow]
  const processedLoadSummaries = processLoadSummaries(loadSummaries, paysByVolume, showRecovered, showDefectTons)
  const empty = { text: '', border: noBorder }
  const totalCols = headerRow.length

  processedLoadSummaries.forEach(({ ticket, rowContent }, index) => {
    body.push(rowContent)

    const isLastRowForTicket = processedLoadSummaries[index + 1]?.ticket?.ticketId !== ticket?.ticketId
    if (showDefectTons && ticket?.defects?.length > 0 && isLastRowForTicket) {
      body.push([
        empty, empty, empty, empty, empty,
        loadDefectsTable(ticket),
        ...Array(totalCols - 6).fill(empty)
      ])
    }
  })
  return [{
    table: {
      widths: '*',
      body: [
        [{ text: 'Ticket Summary', border: noBorder, bold: true, fontSize: 15 }],
        [{
          table: {
            headerRows: 1,
            widths,
            body
          },
          layout: alternatingRowColorsLayout,
          border: noBorder
        }]
      ]
    },
    style: 'nestedTable'
  }]
}

const isByproductContract = (contractType) => contractType === ContractType.ByproductSale.value ||
  contractType === ContractType.ByproductPurchase.value ||
  contractType === ContractType.ByproductHauling.value

const scaleDataSourceIcon = (scaleDataSource) => {
  switch (scaleDataSource) {
    case ScaleDataSource.Scaled.value: return rulerSquare
    case ScaleDataSource.Expansion.value: return alphaECircleOutline
    case ScaleDataSource.Imported.value: return fileImport
  }
}

export const scaleDataSourceLegendColumns = (contracts, settlementReportMode) => {
  if (settlementReportMode !== SettlementReportMode.Brief.value) return []

  const logContracts = contracts.filter(c => !isByproductContract(c.type))

  return ScaleDataSource.enums
    .filter(source => logContracts.some(c => c.loadSummaries.some(ls => ls.scaleDataSource === source.value)))
    .flatMap(source => ([
      { svg: generateSvgForPdf(scaleDataSourceIcon(source.value)), width: 9 },
      { text: source.name, width: 'auto', fontSize: 8, bold: true, marginLeft: 2, marginRight: 8 }
    ]))
}

function getBriefLoadSummaryTable (loadSummaries, isByproduct) {
  const headerRow = [
    { text: '#', bold: true, border: bottomBorder },
    ...(isByproduct ? [] : [{ text: '', bold: true, border: bottomBorder }]),
    { text: 'Delivered', bold: true, border: bottomBorder },
    { text: 'Driver', bold: true, border: bottomBorder },
    { text: 'Product', bold: true, border: bottomBorder },
    ...(isByproduct ? [] : [{ text: 'Pieces', alignment: 'right', bold: true, border: bottomBorder }]),
    { text: 'Gross Tons', alignment: 'right', bold: true, border: bottomBorder },
    { text: 'Net Tons', alignment: 'right', bold: true, border: bottomBorder },
    ...(isByproduct ? [] : [
      { text: 'Gross MBF', alignment: 'right', bold: true, border: bottomBorder },
      { text: 'Net MBF', alignment: 'right', bold: true, border: bottomBorder }
    ]),
    { text: 'Amount', alignment: 'right', bold: true, border: bottomBorder }
  ]
  const widths = [
    'auto',
    ...(isByproduct ? [] : ['auto']),
    'auto', '*', '*',
    ...(isByproduct ? [] : ['auto']),
    'auto', 'auto',
    ...(isByproduct ? [] : ['auto', 'auto']),
    'auto'
  ]

  const totals = {
    pieces: 0,
    grossTons: 0,
    netTons: 0,
    grossMbf: 0,
    netMbf: 0,
    amount: 0
  }

  const body = [headerRow]
  loadSummaries.forEach(loadSummary => {
    totals.pieces += loadSummary.pieces ?? 0
    totals.grossTons += loadSummary.inWeight - loadSummary.outWeight
    totals.netTons += loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight
    totals.grossMbf += loadSummary.grossVolume ?? 0
    totals.netMbf += loadSummary.netVolume ?? 0
    totals.amount += loadSummary.amount

    let ticketNumber = loadSummary.ticketNumber.toString()
    if (loadSummary.extTicketNumber1 !== '') {
      ticketNumber = `${ticketNumber} (${loadSummary.extTicketNumber1})`
    }
    if (loadSummary.extTicketNumber2 !== '') {
      ticketNumber = `${ticketNumber} (${loadSummary.extTicketNumber2})`
    }

    const icon = scaleDataSourceIcon(loadSummary.scaleDataSource)
    const driver = [loadSummary.driverName, loadSummary.trailerIdentifier].filter(d => d).join(', ')

    body.push([
      { text: ticketNumber, border: noBorder },
      ...(isByproduct ? [] : [icon
        ? { svg: generateSvgForPdf(icon), width: 9, border: noBorder }
        : { text: '', border: noBorder }
      ]),
      { text: utcToLocalDate(loadSummary.weighedOutAt), border: noBorder },
      { text: driver, border: noBorder },
      { text: loadSummary.product ? loadSummary.product : 'Other', border: noBorder },
      ...(isByproduct ? [] : [{ text: numberWithCommas(loadSummary.pieces ?? 0), alignment: 'right', border: noBorder }]),
      { text: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight), alignment: 'right', border: noBorder },
      { text: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight), alignment: 'right', border: noBorder },
      ...(isByproduct ? [] : [
        { text: mbfStringFromBf(loadSummary.grossVolume ?? 0), alignment: 'right', border: noBorder },
        { text: mbfStringFromBf(loadSummary.netVolume ?? 0), alignment: 'right', border: noBorder }
      ]),
      { text: formatMoney(loadSummary.amount), alignment: 'right', border: noBorder }
    ])
  })

  const footerRowStyling = { bold: true, fontSize: 9, border: noBorder }
  body.push([
    { text: 'Totals', ...footerRowStyling },
    ...(isByproduct ? [] : [{ text: '', ...footerRowStyling }]),
    { text: '', ...footerRowStyling },
    { text: '', ...footerRowStyling },
    { text: '', ...footerRowStyling },
    ...(isByproduct ? [] : [{ text: numberWithCommas(totals.pieces), alignment: 'right', ...footerRowStyling }]),
    { text: tonStringFromPounds(totals.grossTons), alignment: 'right', ...footerRowStyling },
    { text: tonStringFromPounds(totals.netTons), alignment: 'right', ...footerRowStyling },
    ...(isByproduct ? [] : [
      { text: mbfStringFromBf(totals.grossMbf), alignment: 'right', ...footerRowStyling },
      { text: mbfStringFromBf(totals.netMbf), alignment: 'right', ...footerRowStyling }
    ]),
    { text: formatMoney(totals.amount), alignment: 'right', ...footerRowStyling }
  ])

  return [{
    table: {
      widths: '*',
      body: [
        [{ text: 'Ticket Summary', border: noBorder, bold: true, fontSize: 15 }],
        [{
          table: {
            headerRows: 1,
            widths,
            body
          },
          layout: alternatingRowColorsLayout,
          border: noBorder
        }]
      ]
    },
    style: 'nestedTable'
  }]
}

function processLoadSummaries (loadSummaries, paysByVolume = false, showRecovered = true, showDefectTons = true) {
  const processedLoadSummaries = []
  const totals = {
    grossTons: 0,
    defectTons: 0,
    netTons: 0,
    grossMbf: 0,
    netMbf: 0,
    amount: 0,
    grossAmount: 0
  }
  loadSummaries.forEach(loadSummary => {
    totals.grossTons += loadSummary.inWeight - loadSummary.outWeight
    totals.defectTons += loadSummary.defectWeight
    totals.netTons += loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight
    totals.grossMbf += loadSummary.grossVolume ?? 0
    totals.netMbf += loadSummary.netVolume ?? 0

    loadSummary.mismanufacturedDefectWeight = (loadSummary.defects ?? [])
      .filter(d => d.category === DefectCategory.Mismanufactured.value)
      .reduce((acc, d) => acc + d.totalLBS, 0)
    loadSummary.natureDefectWeight = (loadSummary.defects ?? [])
      .filter(d => d.category === DefectCategory.Nature.value)
      .reduce((acc, d) => acc + d.totalLBS, 0)

    let ticketNumber = loadSummary.ticketNumber.toString()
    if (loadSummary.extTicketNumber1 !== '') {
      ticketNumber = `${ticketNumber} (${loadSummary.extTicketNumber1})`
    }
    if (loadSummary.extTicketNumber2 !== '') {
      ticketNumber = `${ticketNumber} (${loadSummary.extTicketNumber2})`
    }

    const hasMultipleActivities = loadSummary.paymentDetails.length > 1
    if (hasMultipleActivities) {
      processedLoadSummaries.push({
        ticket: loadSummary,
        rowContent: [
          { text: ticketNumber, border: noBorder, bold: true },
          { text: utcToLocalDate(loadSummary.weighedOutAt), border: noBorder, bold: true },
          { text: '', border: noBorder, bold: true },
          { text: loadSummary.product ? loadSummary.product : 'Other', border: noBorder, bold: true },
          { text: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight), alignment: 'right', border: noBorder, bold: true },
          ...(showDefectTons ? [{ text: tonStringFromPounds(loadSummary.defectWeight), alignment: 'right', border: noBorder, bold: true }] : []),
          { text: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight), alignment: 'right', border: noBorder, bold: true },
          ...(paysByVolume ? [
            { text: mbfStringFromBf(loadSummary.grossVolume ?? 0), alignment: 'right', border: noBorder, bold: true },
            { text: mbfStringFromBf(loadSummary.netVolume ?? 0), alignment: 'right', border: noBorder, bold: true }
          ] : []),
          { text: '--', border: noBorder, alignment: 'right', bold: true },
          { text: '--', alignment: 'right', border: noBorder, bold: true },
          ...(showRecovered ? [{ text: formatMoney(loadSummary.grossAmount - loadSummary.amount), alignment: 'right', border: noBorder, bold: true }] : []),
          { text: formatMoney(loadSummary.amount), alignment: 'right', border: noBorder, bold: true }
        ]
      })
    }

    for (const pd of loadSummary.paymentDetails) {
      const shouldShowActivity = (pd.computedAtPayOn === PayOn.Defect.value && loadSummary.defectWeight > 0) ||
        (pd.computedAtPayOn === PayOn.NatureDefect.value && loadSummary.defects?.some(d => d.category === DefectCategory.Nature.value)) ||
        (pd.computedAtPayOn === PayOn.MismanufacturedDefect.value && loadSummary.defects?.some(d => d.category === DefectCategory.Mismanufactured.value)) ||
        (pd.computedAtPayOn === PayOn.Net.value || pd.computedAtPayOn === PayOn.Gross.value || pd.computedAtPayBy === PayBy.Load.value)

      // Only skip if there are multiple activities and the activity should not be shown.
      if (!shouldShowActivity && hasMultipleActivities) {
        continue
      }

      totals.amount += pd.amount
      totals.grossAmount += pd.grossAmount

      const payOnUnit = getUnitTextForActivity({
        payOn: pd.computedAtPayOn,
        payBy: pd.computedAtPayBy,
        modifier: pd.computedAtModifier,
        grossTons: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight),
        defectTons: tonStringFromPounds(loadSummary.defectWeight),
        netTons: tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight),
        grossVolume: loadSummary.grossVolume,
        netVolume: loadSummary.netVolume,
        mismanufactureDefectTons: tonStringFromPounds(loadSummary.mismanufacturedDefectWeight),
        natureDefectTons: tonStringFromPounds(loadSummary.natureDefectWeight)
      })

      let rate = ''
      let activity = pd.activity
      if (pd.computedAtModifier === ActivityModifier.DistanceTraveled.value) {
        rate = `${formatMoney(pd.computedAtRate)} x ${pd.computedAtModifier}mi.`
      } else {
        if (pd.computedAtPayBy === PayBy.Volume.value) {
          const pdAmount = pd.deductFromPayableId ? Math.abs(pd.amount) : pd.amount
          rate = pd.computedAtPayOn === PayOn.Gross.value
            ? formatMoney(pdAmount / (loadSummary.grossVolume / NumericConstants.MbfDivisor))
            : formatMoney(pdAmount / (loadSummary.netVolume / NumericConstants.MbfDivisor))
        } else {
          rate = formatMoney(pd.computedAtRate)
        }
      }

      if (pd.computedAtSplit > 0 && pd.computedAtSplit < 100) {
        const formattedSplit = formatPercentage(pd.computedAtSplit)
        rate = `${rate} x ${formattedSplit}`
        activity = `${activity} (${formattedSplit})`
      }

      processedLoadSummaries.push({
        ticket: loadSummary,
        rowContent: [
          { text: hasMultipleActivities ? '' : ticketNumber, border: noBorder },
          { text: hasMultipleActivities ? '' : utcToLocalDate(loadSummary.weighedOutAt), border: noBorder },
          { text: activity, border: noBorder },
          { text: hasMultipleActivities ? '' : loadSummary.product, border: noBorder },
          { text: hasMultipleActivities ? '' : tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight), alignment: 'right', border: noBorder, bold: true },
          ...(showDefectTons ? [{ text: hasMultipleActivities ? '' : tonStringFromPounds(loadSummary.defectWeight), alignment: 'right', border: noBorder, bold: true }] : []),
          { text: hasMultipleActivities ? '' : tonStringFromPounds(loadSummary.inWeight - loadSummary.outWeight - loadSummary.defectWeight), alignment: 'right', border: noBorder, bold: true },
          ...(paysByVolume ? [
            { text: hasMultipleActivities ? '' : mbfStringFromBf(loadSummary.grossVolume ?? 0), alignment: 'right', border: noBorder },
            { text: hasMultipleActivities ? '' : mbfStringFromBf(loadSummary.netVolume ?? 0), alignment: 'right', border: noBorder }
          ] : []),
          { text: payOnUnit, alignment: 'right', border: noBorder },
          { text: rate, alignment: 'right', border: noBorder },
          ...(showRecovered ? [{ text: formatMoney(pd.grossAmount - pd.amount), alignment: 'right', border: noBorder }] : []),
          { text: formatMoney(pd.amount), alignment: 'right', border: noBorder }
        ]
      })
    }
  })

  // Total Row
  processedLoadSummaries.push({
    ticket: null,
    rowContent: [
      { text: 'Totals', bold: true, fontSize: 9, border: noBorder },
      { text: '', bold: true, fontSize: 9, border: noBorder },
      { text: '', bold: true, fontSize: 9, border: noBorder },
      { text: '', bold: true, fontSize: 9, border: noBorder },
      { text: tonStringFromPounds(totals.grossTons), alignment: 'right', bold: true, fontSize: 9, border: noBorder },
      ...(showDefectTons ? [{ text: tonStringFromPounds(totals.defectTons), alignment: 'right', bold: true, fontSize: 9, border: noBorder }] : []),
      { text: tonStringFromPounds(totals.netTons), alignment: 'right', bold: true, fontSize: 9, border: noBorder },
      ...(paysByVolume ? [
        { text: mbfStringFromBf(totals.grossMbf), alignment: 'right', bold: true, fontSize: 9, border: noBorder },
        { text: mbfStringFromBf(totals.netMbf), alignment: 'right', bold: true, fontSize: 9, border: noBorder }
      ] : []),
      { text: '', border: noBorder },
      { text: '', bold: true, fontSize: 9, border: noBorder },
      ...(showRecovered ? [{ text: formatMoney(totals.grossAmount - totals.amount), alignment: 'right', bold: true, fontSize: 9, border: noBorder }] : []),
      { text: formatMoney(totals.amount), alignment: 'right', bold: true, fontSize: 9, border: noBorder }
    ]
  })

  return processedLoadSummaries
}

function loadDefectsTable (loadSummary) {
  const values = []

  let defects = loadSummary.defects

  if (loadSummary.defects.length > 0) {
    const firstDefect = defects[0]
    // Check if defect is segmented.
    if (firstDefect.quantity === 0 || firstDefect.quantity === null) {
      defects = Object.values(
        defects.reduce((segmentedDefects, defect) => {
          if (segmentedDefects[defect.name] === undefined) {
            segmentedDefects[defect.name] = {
              ...defect
            }
          } else {
            segmentedDefects[defect.name].totalLBS += defect.totalLBS
          }
          return segmentedDefects
        }, {})
      )
    }
  }

  defects = defects.sort((a, b) => a.category === b.category ? a.name.localeCompare(b.name) : a.category - b.category)

  const defectNameWithCategory = (defect) => {
    switch (defect.category) {
      case DefectCategory.Nature.value:
        return `N - ${defect.name}`
      case DefectCategory.Mismanufactured.value:
        return `M - ${defect.name}`
      default:
        return defect.name
    }
  }

  defects.forEach(defect => {
    values.push(
      [
        { text: defectNameWithCategory(defect), border: noBorder, fontSize: 8, alignment: 'left' },
        { text: tonStringFromPounds(defect.totalLBS), alignment: 'right', border: noBorder, fontSize: 8 }
      ]
    )
  })

  return {
    table: {
      widths: ['*', 'auto'],
      body: values
    },
    alignment: 'right',
    border: noBorder,
    layout: {
      paddingRight: () => 0
    }
  }
}

export function getFooter (page, pages, includeDate = true, legendColumns = []) {
  return {
    marginRight: 50,
    marginLeft: 50,
    columns: [
      {
        width: '*',
        text: `${page}/${pages}`,
        fontSize: 8,
        bold: true
      },
      ...legendColumns,
      includeDate ? {
        width: '*',
        text: `As of ${utcToLocalDate(new Date().toISOString())}`,
        alignment: 'right',
        fontSize: 8,
        bold: true
      } : undefined
    ].filter(d => d !== undefined)
  }
}

export const getHeaderForSettlement = (dateInfo, payee, contract = undefined) => {
  const { address } = payee
  const payeeInfoTable = {
    table: {
      body: [
        [{ text: 'Payee: ' }, { text: `${payee.payeeName}` }]
      ]
    },
    layout: 'noBorders'
  }

  if (address !== null && address !== undefined) {
    const { addressLine1, addressLine2, city, state, postalCode } = address

    const addressLine = (addressLine2 && addressLine2 !== '')
      ? [[{ text: 'Address: ' }, { text: addressLine1 }], [{ text: '' }, { text: addressLine2 }]]
      : [[{ text: 'Address: ' }, { text: addressLine1 }]]

    payeeInfoTable.table.body.push(
      ...addressLine,
      [{ text: '' }, { text: `${city}, ${state} ${postalCode}` }]
    )
  } else {
    payeeInfoTable.table.body.push(
      [{ text: 'Address: ' }, { text: 'N/A' }]
    )
  }

  const dateInfoTable = getPeriodDateTable(dateInfo)

  return [{
    alignment: 'left',
    table: {
      widths: '*',
      body: [
        [
          payeeInfoTable,
          contract ? getContractTable(contract) : [''],
          dateInfoTable
        ]
      ]
    },
    layout: {
      vLineColor: function (i) {
        if (i === 0 || i === 3) { return 'white' }
        return 'black'
      },
      hLineColor: function () {
        return 'white'
      }
    }
  }]
}

export const getDateInfo = ({ periodStartDate, periodEndDate, payDate }, exportBatch) => {
  const subtractDay = (date) => {
    const formattedDate = new Date(date)
    return formattedDate.setDate(formattedDate.getDate() - 1)
  }
  const endDate = subtractDay(periodEndDate)
  const formattedPayDate = subtractDay(payDate)
  return {
    startDate: utcToLocalDate(periodStartDate),
    endDate: utcToLocalDate(endDate),
    payDate: utcToLocalDate(formattedPayDate),
    exportBatchLabel: exportBatch?.label,
    exportDate: exportBatch?.exportDate ? utcToLocalDate(exportBatch.exportDate) : undefined
  }
}

export const singlePayeeEbSettlementTitle = ({ payee, exportDate, label }) => {
  const baseFilename = `${exportDate}-${label}`
  const filenamePrefix = fileNameForString(payee)
  return `${filenamePrefix}-${fileNameForString(baseFilename)}`.replace(/\s/g, '')
}

export const nestedTableStyling = {
  layout: {
    hLineWidth: function (i, node) {
      return (i === 0 || i === node.table.body.length) ? 2 : 1
    },
    vLineWidth: function (i, node) {
      return (i === 0 || i === node.table.widths.length) ? 2 : 1
    },
    hLineColor: function (i, node) {
      return (i % 2 !== 0 || i === 0 || i === node.table.body.length) ? 'black' : 'white'
    },
    vLineColor: function () {
      return 'black'
    }
  },
  style: 'nestedTable'
}

export const defaultDocumentConfig = {
  header: getHeader,
  footer: getFooter,
  pageMargins: [40, 80, 40, 30],
  pageOrientation: 'landscape',
  headerRows: 1,
  styles: {
    header: {
      fontSize: 100
    },
    rightAlign: {
      alignment: 'right'
    },
    nestedTable: {
      fontSize: 8
    }
  }
}

export function getAdditionalFinancialTables (tractPayables, advances, corrections, recoveryInfo, accountPayables, params = {}) {
  const { isByproducts = false, unexported = false } = params
  tractPayables ??= []
  advances ??= []
  corrections ??= []
  recoveryInfo ??= []
  accountPayables ??= []
  const additionalTables = []
  if (tractPayables.length > 0) {
    additionalTables.push({
      table: getTractPaymentTable(tractPayables),
      type: 'tp'
    })
  }

  if (advances.length > 0 || recoveryInfo.length > 0) {
    additionalTables.push({
      table: getAdvanceInfoTables(advances, recoveryInfo, unexported),
      type: 'ad'
    })
  }

  if (corrections.length > 0) {
    additionalTables.push({
      table: getCorrectionsTable(corrections, { isByproducts }),
      type: 'co'
    })
  }

  if (accountPayables.length > 0) {
    additionalTables.push({
      table: getAccountPaymentTable(accountPayables),
      type: 'ap'
    })
  }

  return additionalTables
}

// TODO: BEN - refactor the rest of the financial table methods to look like this
const getCorrectionsTable = (ticketCorrections, params = {}) => {
  const { isByproducts } = params

  ticketCorrections = ticketCorrections.sort((a, b) => {
    if (a.activity === b.activity) {
      return a.correctionSide - b.correctionSide
    }
    return a.activity.localeCompare(b.activity)
  })

  const columns = [
    {
      text: '#',
      value: correction => {
        let ticketNumber = correction.ticketNumber
        if (correction.extTicketNumber1 !== '') ticketNumber = `${ticketNumber} (${correction.extTicketNumber1})`
        if (correction.extTicketNumber2 !== '') ticketNumber = `${ticketNumber} (${correction.extTicketNumber2})`
        return ticketNumber
      },
      width: 'auto'
    },
    {
      text: 'Type',
      value: correction => CorrectionTypes.find(c => c.value === correction.correctionType).name,
      width: '*'
    },
    {
      text: 'Action',
      value: correction => CorrectionSide.find(c => c.value === correction.correctionSide).name,
      width: '*'
    },
    {
      text: 'Activity',
      value: correction => correction.activity,
      width: 'auto'
    },
    {
      text: 'Product',
      value: correction => correction.product,
      width: 'auto'
    },

    !isByproducts ? {
      text: 'Tract',
      value: correction => correction.tract,
      width: 'auto'
    } : undefined,

    {
      text: 'Destination',
      value: correction => correction.destination,
      width: 'auto'
    },

    !isByproducts ? {
      text: 'Setting',
      value: correction => correction.setting,
      width: 'auto'
    } : undefined,

    {
      text: 'Net Tons',
      value: correction => correction.netWeight,
      format: tonStringFromPounds,
      alignment: 'right',
      width: 'auto'
    },
    {
      text: 'Gross Amount',
      value: correction => correction.grossAmount,
      format: formatMoney,
      alignment: 'right',
      width: 'auto'
    },
    {
      text: 'Amount',
      value: correction => correction.amount,
      format: formatMoney,
      alignment: 'right',
      width: 'auto'
    }
  ].filter(c => c !== undefined)

  const correctionTotals = ticketCorrections.reduce((total, correction) => {
    total.amount += correction.amount
    total.grossAmount += correction.grossAmount
    return total
  }, { amount: 0, grossAmount: 0 })

  const totalRow = Array.from({ length: columns.length - 3 }).map(() => ({ text: '', border: topBorder }))
  totalRow.unshift({ text: 'Total', bold: true, border: topBorder })
  totalRow.push({
    text: formatMoney(correctionTotals.grossAmount),
    bold: true,
    border: topBorder,
    alignment: 'right'
  })
  totalRow.push({
    text: formatMoney(correctionTotals.amount),
    bold: true,
    border: topBorder,
    alignment: 'right'
  })

  const body = [
    columns.map(col => ({
      text: col.text,
      bold: true,
      border: bottomBorder,
      alignment: col.alignment
    })),

    ...ticketCorrections.map(correction => columns.map(col => ({
      text: col.format ? col.format(col.value(correction)) : col.value(correction),
      alignment: col.alignment,
      border: noBorder
    }))),

    totalRow
  ]

  return [
    [[{ text: 'Corrections', border: noBorder, fontSize: 12, bold: true }]],
    [{
      marginBottom: 20,
      table: {
        widths: columns.map(col => col.width),
        body
      },
      layout: alternatingRowColorsLayout
    }]
  ]
}

const getTractPaymentTable = (tractPayables) => {
  const headers = [
    { text: 'Title', bold: true, border: bottomBorder, width: '30%', maxCharsPerLine: 48 },
    { text: 'Tract', bold: true, border: bottomBorder, width: '25%' },
    { text: 'Activity', bold: true, border: bottomBorder, width: '25%', maxCharsPerLine: 28 },
    { text: 'Amount', bold: true, border: bottomBorder, alignment: 'right', width: '20%' }
  ]
  const body = [headers]
  tractPayables.forEach(tractPayment => {
    body.push(headers.map(h => {
      switch (h.text) {
        case 'Title':
          return { text: wrapTextAtMaxCharacters(tractPayment.paymentNote, h.maxCharsPerLine), border: noBorder }
        case 'Tract':
          return { text: tractPayment.tractName, border: noBorder }
        case 'Activity':
          return { text: wrapTextAtMaxCharacters(tractPayment.activityName, h.maxCharsPerLine), border: noBorder }
        case 'Amount':
          return { text: formatMoney(tractPayment.amount), border: noBorder, alignment: 'right' }
      }
    }))
  })

  const totalTractPayment = formatMoney(tractPayables.reduce((total, payable) => {
    total += payable.amount
    return total
  }, 0))

  body.push([
    { text: 'Total', bold: true, border: noBorder },
    { text: '', bold: true, border: noBorder },
    { text: '', bold: true, border: noBorder },
    { text: totalTractPayment, bold: true, border: noBorder, alignment: 'right' }
  ])

  return [
    [[{ text: 'Tract Payments', border: [false, false, false, false], fontSize: 15, bold: true }]],
    [{
      marginBottom: 20,
      table: {
        headerRows: 1,
        widths: headers.map(h => h.width),
        body: body
      },
      layout: alternatingRowColorsLayout
    }]
  ]
}

const getAccountPaymentTable = (accountPayables) => {
  const headers = [
    { text: 'Title', bold: true, border: bottomBorder, width: '50%', maxCharsPerLine: 64 },
    { text: 'Activity', bold: true, border: bottomBorder, width: '30%', maxCharsPerLine: 32 },
    { text: 'Amount', bold: true, border: bottomBorder, alignment: 'right', width: '20%' }
  ]
  const body = [headers]
  accountPayables.forEach(accountPayment => {
    body.push(headers.map(h => {
      switch (h.text) {
        case 'Title':
          return {
            text: wrapTextAtMaxCharacters(accountPayment.paymentNote, h.maxCharsPerLine),
            border: noBorder
          }
        case 'Activity':
          return {
            text: wrapTextAtMaxCharacters(accountPayment.activityName, h.maxCharsPerLine),
            border: noBorder
          }
        case 'Amount':
          return { text: formatMoney(accountPayment.amount), border: noBorder, alignment: 'right' }
      }
    }))
  })

  const totalAccountPayment = formatMoney(accountPayables.reduce((total, payable) => {
    total += payable.amount
    return total
  }, 0))

  body.push([
    { text: 'Total', bold: true, border: noBorder },
    { text: '', bold: true, border: noBorder },
    { text: totalAccountPayment, bold: true, border: noBorder, alignment: 'right' }
  ])

  return [
    [[{ text: 'Account Payments', border: [false, false, false, false], fontSize: 15, bold: true }]],
    [{
      marginBottom: 20,
      table: {
        headerRows: 1,
        widths: headers.map(h => h.width),
        body: body
      },
      layout: alternatingRowColorsLayout
    }]
  ]
}

const getAdvanceInfoTables = (advances, recoveryInfo, unexported) => {
  const totalBody = []
  if (advances.length > 0) {
    totalBody.push(...getAdvanceTable(advances))
  }

  const info = scatterToObject(recoveryInfo, {
    contract: ri => ri.contractRecovered !== 0,
    manual: ri => ri.manualRecovered > 0
  }, { multi: true })

  if (info.contract.length > 0) totalBody.push(...getContractRecoveryTable({ recoveryInfo: info.contract, unexported: unexported }))
  if (info.manual.length > 0) totalBody.push(...getManualRecoveryTable({ recoveryInfo: info.manual, unexported: unexported }))

  return totalBody
}

const getAdvanceTable = (advances) => {
  const advanceBody = [[
    { text: 'Entity', bold: true, border: bottomBorder },
    { text: 'Title', bold: true, border: bottomBorder },
    { text: 'Advanced', bold: true, border: bottomBorder, alignment: 'right' },
    { text: 'Remaining', bold: true, border: bottomBorder, alignment: 'right' }
  ]]
  advances.forEach(advance => {
    advanceBody.push([
      { text: advance.businessEntityName, border: noBorder },
      { text: advance.note, border: noBorder },
      { text: formatMoney(advance.amount), border: noBorder, alignment: 'right' },
      { text: formatMoney(advance.remainingBalance), border: noBorder, alignment: 'right' }
    ])
  })

  const totalAdvanced = advances.reduce((total, advance) => {
    total.amount += advance.amount
    total.remainingBalance += advance.amount - advance.recovered
    return total
  }, {
    amount: 0,
    remainingBalance: 0,
    recovered: 0
  })

  advanceBody.push([
    { text: 'Total', bold: true, border: noBorder },
    { text: '', bold: true, border: noBorder },
    { text: formatMoney(totalAdvanced.amount), bold: true, border: noBorder, alignment: 'right' },
    { text: formatMoney(totalAdvanced.remainingBalance), bold: true, border: noBorder, alignment: 'right' }
  ])

  return [[[{ text: 'New Advances', border: [false, false, false, false], fontSize: 15, bold: true }]],
    [{
      marginBottom: 20,
      table: {
        widths: ['auto', '*', 'auto', 'auto'],
        body: advanceBody
      },
      layout: alternatingRowColorsLayout
    }]
  ]
}

export const getRecoveryTable = (recoveryInfo, getRecovered = r => r.recovered, title = 'Advance Recoveries', unexported = false) => {
  const totalRecovered = recoveryInfo.reduce((total, r) => {
    total.amount += r.amount
    total.balance += r.balance
    total.recovered += getRecovered(r)
    total.previouslyRecoveredAmount += r.previouslyRecoveredAmount ?? 0
    return total
  }, {
    amount: 0,
    balance: 0,
    recovered: 0,
    previouslyRecoveredAmount: 0
  })

  const recoveryBody = [
    [
      { text: 'Entity', bold: true, border: bottomBorder },
      { text: 'Title', bold: true, border: bottomBorder },
      { text: 'Type', bold: true, border: bottomBorder },
      { text: 'Rate', bold: true, border: bottomBorder, alignment: 'right' },
      { text: 'Advanced', bold: true, border: bottomBorder, alignment: 'right' },
      { text: 'Previously Recovered', bold: true, border: bottomBorder, alignment: 'right' },
      { text: unexported ? 'Total Recovered' : 'Recovered in Batch', bold: true, border: bottomBorder, alignment: 'right' },
      { text: 'Balance', bold: true, border: bottomBorder, alignment: 'right' }
    ],

    ...recoveryInfo.map(r => ([
      { text: r.businessEntityName, border: noBorder },
      { text: r.note, border: noBorder },
      { text: RecoveryType.fromInt(r.type), border: noBorder, alignment: 'right' },
      { text: recoveryRateText(r.rate, r.type), border: noBorder, alignment: 'right' },
      { text: formatMoney(r.amount), border: noBorder, alignment: 'right' },
      { text: formatMoney(r.previouslyRecoveredAmount ?? 0), border: noBorder, alignment: 'right' },
      { text: formatMoney(getRecovered(r)), border: noBorder, alignment: 'right' },
      { text: formatMoney(r.balance), border: noBorder, alignment: 'right' }
    ])),

    [
      { text: 'Total', bold: true, border: noBorder },
      { text: '', bold: true, border: noBorder },
      { text: '', bold: true, border: noBorder },
      { text: '', bold: true, border: noBorder },
      { text: formatMoney(totalRecovered.amount), bold: true, border: noBorder, alignment: 'right' },
      { text: formatMoney(totalRecovered.previouslyRecoveredAmount), bold: true, border: noBorder, alignment: 'right' },
      { text: formatMoney(totalRecovered.recovered), bold: true, border: noBorder, alignment: 'right' },
      { text: formatMoney(totalRecovered.balance), bold: true, border: noBorder, alignment: 'right' }
    ]
  ]

  return [[[{ text: title, border: [false, false, false, false], fontSize: 15, bold: true }]],
    [{
      marginBottom: 20,
      table: {
        widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
        body: recoveryBody
      },
      layout: alternatingRowColorsLayout
    }]
  ]
}

export const getFileNameForPeriod = ({ startDate, endDate, exportBatchLabel, exportDate }) => {
  if (exportDate === undefined) {
    return `unexported-${startDate}-${endDate}`
  }

  const label = exportBatchLabel === undefined || exportBatchLabel === null || exportBatchLabel === ''
    ? ''
    : `-${exportBatchLabel}`

  return fileNameForString(`${exportDate}${label}`)
}

export const getContractRecoveryTable = ({ recoveryInfo, unexported }) => getRecoveryTable(recoveryInfo, r => r.contractRecovered, 'Contract Recoveries', unexported)
export const getManualRecoveryTable = ({ recoveryInfo, unexported }) => getRecoveryTable(recoveryInfo, r => r.manualRecovered, 'Manual Recoveries', unexported)

export const getUnitTextForActivity = ({
  payOn,
  payBy,
  modifier,
  grossTons,
  defectTons,
  mismanufactureDefectTons,
  natureDefectTons,
  netTons,
  netVolume,
  grossVolume
}, distance, includeLabel = true) => {
  if (payBy === PayBy.Load.value) return '--'
  if (modifier === ActivityModifier.DistanceTraveled.value) { return i18n.t('distanceInMiles', { distance: distance }) }
  if (payBy === PayBy.Volume.value) {
    return payOn === PayOn.Gross.value
      ? includeLabel ? `Gross - ${numberWithCommas(grossVolume / NumericConstants.MbfDivisor, 3)} MBF` : `${numberWithCommas(grossVolume / NumericConstants.MbfDivisor, 3)} MBF`
      : includeLabel ? `Net - ${numberWithCommas(netVolume / NumericConstants.MbfDivisor, 3)} MBF` : `${numberWithCommas(netVolume / NumericConstants.MbfDivisor, 3)} MBF`
  }

  let weight = 0
  switch (payOn) {
    case PayOn.Gross.value: weight = grossTons; break
    case PayOn.Net.value: weight = netTons; break
    case PayOn.Defect.value: weight = defectTons; break
    case PayOn.NatureDefect.value: weight = natureDefectTons; break
    case PayOn.MismanufacturedDefect.value: weight = mismanufactureDefectTons; break
    default: weight = netTons
  }

  return includeLabel
    ? `${abbreviationForPayOnType(payOn)} - ${weight}`
    : `${weight}t`
}

export const getPeriodDateTable = (dateInfo) => {
  const body = []

  const payPeriodLength = Math.abs(moment(dateInfo.endDate).diff(moment(dateInfo.startDate), 'days'))
  if (payPeriodLength <= 21) {
    body.push(
      [{ text: 'Pay Date: ' }, { text: dateInfo.payDate }],
      [{ text: 'Pay Period Start: ' }, { text: dateInfo.startDate }],
      [{ text: 'Pay Period End:' }, { text: dateInfo.endDate }]
    )
  }

  if (dateInfo?.exportBatchLabel !== '' && dateInfo?.exportBatchLabel !== undefined) {
    body.push([{ text: 'Export Batch:' }, { text: dateInfo.exportBatchLabel }])
  }
  if (dateInfo?.exportDate !== undefined) {
    body.push([{ text: 'Export Date:' }, { text: dateInfo.exportDate }])
  }

  return {
    alignment: 'left',
    table: { body },
    layout: 'noBorders'
  }
}

const abbreviationForPayOnType = (payOn) => {
  switch (payOn) {
    case PayOn.Gross.value: return 'Gross'
    case PayOn.Net.value: return 'Net'
    case PayOn.Defect.value: return 'D'
    case PayOn.NatureDefect.value: return 'N'
    case PayOn.MismanufacturedDefect.value: return 'M'
  }
}

export const fileNameForString = (str, extension = null) => {
  return extension
    ? `${str.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')}.${extension}`
    : str.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')
}
